import React, { useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

const plans = [
  {
    name: 'Starter',
    monthlyPrice: 0,
    yearlyPrice: 0,
    desc: 'Perfect for trying Director Discovery and small prospecting tasks.',
    credits: '50 credits/month',
    cta: 'Get started free',
    ctaStyle: 'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
    features: [
      '50 email finder credits',
      'Email verification',
      'Chrome extension',
      'CSV export',
      'API access',
    ],
    highlight: false,
  },
  {
    name: 'Growth',
    monthlyPrice: 49,
    yearlyPrice: 39,
    desc: 'For individuals and small teams doing consistent outbound.',
    credits: '1,000 credits/month',
    cta: 'Start free trial',
    ctaStyle: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200',
    features: [
      '1,000 email + mobile credits',
      'Bulk enrichment (CSV)',
      'Domain search',
      'CRM integrations',
      'Priority support',
      'Intent signals access',
    ],
    highlight: true,
    badge: 'Most Popular',
  },
  {
    name: 'Pro',
    monthlyPrice: 99,
    yearlyPrice: 79,
    desc: 'For growing sales teams that need scale and collaboration.',
    credits: '5,000 credits/month',
    cta: 'Start free trial',
    ctaStyle: 'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
    features: [
      '5,000 email + mobile credits',
      'Team seats (up to 5)',
      'Advanced filters & intent',
      'Salesforce & HubSpot sync',
      'API at scale',
      'Dedicated onboarding',
    ],
    highlight: false,
  },
];

export default function PricingTeaser() {
  const [yearly, setYearly] = useState(false);

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm text-indigo-600 font-semibold uppercase tracking-wider mb-2">Pricing</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto mb-8">
            Only pay for verified emails and mobile numbers. No hidden fees, no surprise charges.
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center bg-gray-200 rounded-xl p-1 gap-1">
            <button
              onClick={() => setYearly(false)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${!yearly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setYearly(true)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${yearly ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Yearly
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">Save 20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 flex flex-col transition-all ${
                plan.highlight
                  ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-200 scale-[1.03] z-10'
                  : 'bg-white border border-gray-100 shadow-sm hover:shadow-md'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className={`text-lg font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-4 ${plan.highlight ? 'text-indigo-200' : 'text-gray-500'}`}>
                  {plan.desc}
                </p>
                <div className="flex items-end gap-1">
                  <span className={`text-4xl font-extrabold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                    ${yearly ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  {plan.monthlyPrice > 0 && (
                    <span className={`text-sm mb-1.5 ${plan.highlight ? 'text-indigo-200' : 'text-gray-400'}`}>
                      /month
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-1 font-medium ${plan.highlight ? 'text-indigo-300' : 'text-gray-400'}`}>
                  {plan.credits}
                </p>
              </div>

              <ul className="space-y-3 flex-1 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.highlight ? 'bg-white/20' : 'bg-indigo-50'}`}>
                      <CheckIcon className={`w-3 h-3 ${plan.highlight ? 'text-white' : 'text-indigo-600'}`} />
                    </div>
                    <span className={`text-sm ${plan.highlight ? 'text-indigo-100' : 'text-gray-600'}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#"
                className={`w-full text-center py-3 rounded-xl text-sm font-bold transition-all ${plan.ctaStyle}`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-gray-400 mt-8">
          Need more? <a href="#" className="text-indigo-600 hover:underline font-medium">Contact us for Enterprise pricing →</a>
        </p>
      </div>
    </section>
  );
}
